import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  useDeleteOfferInventoryLimits,
  useGetOfferInventoryLimits,
  useSetBulkOfferInventoryLimits,
  useSetOfferInventoryLimits,
} from '~/hooks/apis/reservation/useOfferInventoryLimits';

import OfferInventoryCalendar from './OfferInventoryCalendar';

interface Props {
  offerId: string;
  propertyId: string;
  roomType: App.RoomType;
}

function sortedDateArray(dates: Set<string>): Array<string> {
  return Array.from(dates).sort();
}

export default function OfferInventoryPane({ offerId, propertyId, roomType }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState<'free' | 'range'>('range');

  const { data: inventoryLimits = [], isLoading } = useGetOfferInventoryLimits(offerId, propertyId, roomType.id);
  const setInventoryMutation = useSetOfferInventoryLimits(offerId, propertyId, roomType.id);
  const setBulkInventoryMutation = useSetBulkOfferInventoryLimits(offerId, propertyId, roomType.id);
  const deleteInventoryMutation = useDeleteOfferInventoryLimits(offerId, propertyId, roomType.id);

  const selectedSorted = sortedDateArray(selectedDates);
  const selectionStart = selectedSorted[0] ?? '';
  const selectionEnd = selectedSorted[selectedSorted.length - 1] ?? '';

  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [inventoryTotal, setInventoryTotal] = useState('');
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const handleSetInventory = async () => {
    const totalNum = parseInt(inventoryTotal, 10);
    if (!selectionStart || isNaN(totalNum) || totalNum < 0) {
      return;
    }
    try {
      if (selectMode === 'range') {
        await setInventoryMutation.mutateAsync({ start_date: selectionStart, end_date: selectionEnd, total: totalNum });
      } else {
        await setBulkInventoryMutation.mutateAsync({ dates: selectedSorted, total: totalNum });
      }
      enqueueSnackbar('Inventory limits updated', { variant: 'success' });
      setInventoryDialogOpen(false);
      setInventoryTotal('');
      setSelectedDates(new Set());
    } catch {
      enqueueSnackbar('Failed to set inventory limits', { variant: 'error' });
    }
  };

  const handleClearAllInventory = async () => {
    try {
      await deleteInventoryMutation.mutateAsync(undefined);
      enqueueSnackbar('Inventory limits cleared', { variant: 'success' });
      setConfirmClearOpen(false);
    } catch {
      enqueueSnackbar('Failed to clear inventory limits', { variant: 'error' });
    }
  };

  const handleClearInventory = async (dates: Array<string>) => {
    try {
      if (selectMode === 'range') {
        const sorted = [...dates].sort();
        await deleteInventoryMutation.mutateAsync({ start_date: sorted[0], end_date: sorted[sorted.length - 1] });
      } else {
        // Sequential for the same reason as handleSetInventory above.
        // TODO [XTRNT-970]: turn this into a batched call when backend supports it
        const results: Array<PromiseSettledResult<Awaited<ReturnType<typeof deleteInventoryMutation.mutateAsync>>>> =
          [];
        for (const date of dates) {
          try {
            const value = await deleteInventoryMutation.mutateAsync({ start_date: date, end_date: date });
            results.push({ status: 'fulfilled', value });
          } catch (reason) {
            results.push({ status: 'rejected', reason });
          }
        }
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed > 0) {
          enqueueSnackbar(`${failed} date(s) failed to clear`, { variant: 'error' });
        }
        if (failed < dates.length) {
          enqueueSnackbar(`${dates.length - failed} date(s) cleared`, { variant: 'success' });
        }
        setSelectedDates(new Set());
        return;
      }
      enqueueSnackbar('Inventory limits cleared', { variant: 'success' });
      setSelectedDates(new Set());
    } catch {
      enqueueSnackbar('Failed to clear inventory limits', { variant: 'error' });
    }
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  return (
    <Stack spacing={3}>
      {inventoryLimits.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {inventoryLimits.length} inventory limit{inventoryLimits.length !== 1 ? 's' : ''} set
          </Typography>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => setConfirmClearOpen(true)}
            disabled={deleteInventoryMutation.isPending}
          >
            Clear all limits
          </Button>
        </Stack>
      )}

      <OfferInventoryCalendar
        inventoryLimits={inventoryLimits}
        selectedDates={selectedDates}
        selectMode={selectMode}
        onSelectModeChange={setSelectMode}
        onDatesChange={setSelectedDates}
        onSetInventory={() => {
          setInventoryTotal('');
          setInventoryDialogOpen(true);
        }}
        onClearInventory={(dates) => handleClearInventory(dates)}
      />

      <Dialog open={inventoryDialogOpen} onClose={() => setInventoryDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {inventoryLimits.some((l) => selectedDates.has(l.date)) ? 'Update Inventory Limits' : 'Set Inventory Limits'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="info">
              Applying to {selectedDates.size} date{selectedDates.size !== 1 ? 's' : ''}: {selectionStart}
              {selectionStart !== selectionEnd ? ` – ${selectionEnd}` : ''}
            </Alert>
            <TextField
              label="Total rooms available"
              type="number"
              value={inventoryTotal}
              onChange={(e) => setInventoryTotal(e.target.value)}
              inputProps={{ min: 0 }}
              fullWidth
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInventoryDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSetInventory}
            variant="contained"
            disabled={setInventoryMutation.isPending || setBulkInventoryMutation.isPending || inventoryTotal === ''}
          >
            {setInventoryMutation.isPending || setBulkInventoryMutation.isPending ? 'Saving…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Clear all inventory limits?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete all {inventoryLimits.length} inventory limit
            {inventoryLimits.length !== 1 ? 's' : ''} for this room type. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
          <Button
            onClick={handleClearAllInventory}
            variant="contained"
            color="error"
            disabled={deleteInventoryMutation.isPending}
          >
            {deleteInventoryMutation.isPending ? 'Clearing…' : 'Clear all'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
